import dayjs from "dayjs"
import Duration from "dayjs/plugin/duration"
import LocalizedFormat from "dayjs/plugin/localizedFormat"
import RelativeTime from "dayjs/plugin/relativeTime"
import UTC from "dayjs/plugin/utc"
import WeekOfYear from "dayjs/plugin/weekOfYear"

import type {
  DateArgument,
  DateDisplayFormat,
  Dayjs,
  Timestamp,
  UnitType
} from "./date.helpers.types"

export const DEFAULT_FORMAT = "ll"
export const DEFAULT_LONG_FORMAT = "lll"
export const MONTH_DAY_FORMAT = "MMM DD"
export const MONTH_DAY_SHORT_FORMAT = "MMM D"
export const MONTH_DAY_TIME_FORMAT = "MMM DD, LT"
export const MONTH_YEAR = "MMM YYYY"
export const MONTH_DAY_YEAR_FORMAT = "MMM DD, YYYY"
export const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss"
export const DATE_TIME_SHORT_FORMAT = "YYYY-MM-DD HH:mm"
export const FILENAME_DATE_TIME_FORMAT = "YYYY-MM-DD_HHmmss"
export const ISO_DATE_FORMAT = "YYYY-MM-DD"

export const DURATION_KEYS = [
  "years",
  "months",
  "days",
  "hours",
  "minutes",
  "seconds"
] as const

export const setPluginsDateHelpers = () => {
  dayjs.extend(RelativeTime)
  dayjs.extend(LocalizedFormat)
  dayjs.extend(UTC)
  dayjs.extend(Duration)
  dayjs.extend(WeekOfYear)
}

export const getDate = (date?: DateArgument) => dayjs(date)

export const getUnixDate = (date?: NonNullable<DateArgument>) =>
  getDate(date).unix()

export const getUTCDate = (date?: NonNullable<DateArgument>) =>
  getDate(date).utc()

export const getDisplayValue = (
  date: DateArgument | "Invalid Date",
  fn: (data: Dayjs) => string
) => {
  try {
    if (!date || date === "Invalid Date") return ""

    const dateObj =
      typeof date === "number" || typeof date === "string"
        ? getDate(date)
        : date

    const result = dateObj.toString()

    if (result === "Invalid Date") return ""

    return fn(dateObj)
  } catch {
    return ""
  }
}

export const getDateInFormat = (
  date: DateArgument,
  format: DateDisplayFormat
) => getDisplayValue(getDate(date), (d) => d.format(format))

// Date pickers hold a Dayjs; APIs take the calendar date
export const toIsoDate = (value: unknown) => {
  if (dayjs.isDayjs(value)) return value.format(ISO_DATE_FORMAT)
  if (typeof value === "string" && value) return value

  return undefined
}

export const getTimestampInMillisecondsFromSecond = (
  timestampInSeconds: Timestamp
) => timestampInSeconds * 1000

export const getTimestamp = (date?: string): Timestamp | undefined => {
  if (date) return getDate(`${date}T12:00:00Z`).unix()
}

export const getDateFromSeconds = (
  timestampInSeconds: Timestamp | undefined
) => {
  if (!timestampInSeconds || timestampInSeconds < 86400) return null

  return getDate(getTimestampInMillisecondsFromSecond(timestampInSeconds))
}

export const getRelativeTimeFromNow = (date: DateArgument) => {
  return getDisplayValue(date, (d) => dayjs.utc(d).fromNow())
}

export const getDateInUserLocale = (
  date: DateArgument,
  format: DateDisplayFormat
) => {
  return getDisplayValue(date, (d) => dayjs.utc(d).local().format(format))
}

export const getDiffToNow = (date: DateArgument, unit: UnitType = "days") =>
  dayjs().diff(date, unit)

export const getDuration = (
  startDate: DateArgument,
  endDate: DateArgument = getDate()
) => dayjs.duration(getDate(endDate).diff(getDate(startDate)))

const normalizeNumeric = (value: number): number | undefined => {
  if (!Number.isFinite(value)) return undefined

  const abs = Math.abs(value)

  if (abs < 1e12) return value * 1000

  return value
}

export const toMs = (
  input: number | string | Date | null | undefined
): number | undefined => {
  if (input == null) return undefined

  if (typeof input === "number") return normalizeNumeric(input)

  if (typeof input === "string") {
    const trimmed = input.trim()

    if (trimmed === "") return undefined

    if (/^-?\d+(\.\d+)?$/.test(trimmed))
      return normalizeNumeric(Number(trimmed))

    const d = dayjs(trimmed)

    return d.isValid() ? d.valueOf() : undefined
  }

  const d = dayjs(input)

  return d.isValid() ? d.valueOf() : undefined
}
